import Vue from "vue";
import App from "./app.vue";
import VueRouter from "vue-router";
import VeeValidate from "vee-validate";
import VueSweetalert2 from "vue-sweetalert2";
import VueAnalytics from "vue-analytics";
import { ApolloClient } from "apollo-client";
import { createHttpLink } from "apollo-link-http";
import { InMemoryCache } from "apollo-cache-inmemory";
import { setContext } from "apollo-link-context";
import VueApollo from "vue-apollo";
import InstantSearch from "vue-instantsearch";
import VueMultianalytics from "vue-multianalytics";
import Moment from "vue-moment";
import Session from "./services/session";
import routes from "./router";
import SharedComponents from "./sharedComponents";
import Env from "./env.js";
import Utils from "./services/utils.js";
import store from "./store/store.js";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import VueMeta from "vue-meta";
import LogRocket from "logrocket";
import VCalendar from "v-calendar";
import "v-calendar/lib/v-calendar.min.css";
import PusherConfig from "./pusherConfig.js";
import VueStomp from "./plugins/VueStomp/vue-stomp.js";
import createTalkSession from "./talkSession.js";
import VueAwesomeSwiper from "vue-awesome-swiper";
import "swiper/swiper-bundle.css";
import { VTooltip, VClosePopover, VPopover } from "v-tooltip";
import trackEventDirective from "./directives/trackEventDirective";
import "./store/trackingStore.js";

import "./assets/sass/app.scss";

export const EventBus = new Vue(),
  Loader = new Vue(),
  Notify = new Vue(),
  router = new VueRouter({
    routes,
    mode: "history",
  }),
  httpLink = createHttpLink({
    uri: Env.graphQLUri(),
  }),
  authLink = setContext(async (response, { headers }) => {
    const token = Session.getAccessToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  }),
  apolloClient = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
  }),
  apolloProvider = new VueApollo({
    defaultClient: apolloClient,
  });
/*
//     /*
//      * TODO:
//      * implement network requestSanitizer to exclude certain urls/endpoints from logging
//      * implement version tracking that aligns with bugsnag version on client
//      * implement error tracking , send same exceptions from bugsnag to logrocket as well
//      * ticket: https://codetribellc.atlassian.net/browse/BUL-2366
//      */
// });

Vue.use(VueStomp);
Vue.use(VCalendar, {
  datePickerShowDayPopover: false,
});
Vue.use(VueMeta, {
  waitOnDestroyed: true,
});
Vue.use(VueRouter);
Vue.use(SharedComponents);

// warning: Changing the `fieldsBagName` will change the global variable that `veeValidate` uses to store the fields, and is used by our own code.
Vue.use(VeeValidate, { fieldsBagName: "veeFields" });
Vue.use(InstantSearch);
Vue.use(Moment);
Vue.use(VueApollo);
Vue.use(VueSweetalert2, {
  allowOutsideClick: false,
  showCloseButton: true,
  animation: false,
});
Vue.use(VueAnalytics, {
  id: Env.googleAnalytics(),
  router,
});
Vue.use(VueMultianalytics, {
  modules: {
    mixpanel: {
      token: Env.mixpanelToken(),
    },
  },
});

Bugsnag.start({
  apiKey: Env.bugsnagApiKey(),
  plugins: [new BugsnagPluginVue()],
  releaseStage: Env.getOrigin(),
  onError: (event) => {
    if (LogRocket && Env.getOrigin() === "prod") {
      LogRocket.getSessionURL((url) => {
        event.addMetadata("Log Rocket", {
          URL: url,
        });
      });
      return event;
    }
  },
});

// eslint-disable-next-line one-var
const bugsnagVue = Bugsnag.getPlugin("vue");
bugsnagVue.installVueErrorHandler(Vue);

Vue.use(VueAwesomeSwiper);
Vue.directive("tooltip", VTooltip);
Vue.directive("close-popover", VClosePopover);
Vue.component("VPopover", VPopover);

Vue.directive("trackevent", trackEventDirective);
Vue.config.productionTip = false;

Vue.prototype.$bugsnagClient = Bugsnag;
Vue.prototype.$session = Session;
Vue.prototype.$eventBus = EventBus;
Vue.prototype.$env = Env;
Vue.prototype.$utils = Utils;
Vue.prototype.$loader = Loader;
Vue.prototype.$notify = Notify;

// eslint-disable-next-line complexity
router.beforeEach((to, from, next) => {
  if (to.meta.keepScrollPosition === undefined || !to.meta.keepScrollPosition) {
    window.scrollTo(0, 0);
  }

  if (to.meta.isPublic) {
    if (from.name === "buyer" && from.query.brandUrl && to.name === "signIn" && !to.query.redirectUrl) {
      next({
        name: to.name,
        query: {
          redirectUrl: router.resolve({
            name: "brand-bio",
            params: {
              brandUrl: from.query.brandUrl,
            },
          }).href,
        },
      });
    } else {
      next();
    }
  } else {
    const user = JSON.parse(localStorage.getItem("USER_INFO"));
    let access = false;

    if (user) {
      for (let i = 0; i < to.meta.roles.length; i++) {
        if (user.role === to.meta.roles[i]) {
          access = true;

          break;
        } else {
          access = false;
        }
      }

      if (access) {
        next();
      } else {
        Session.destroy();
        window.location.href = "/sign-in";
      }
    } else if (to.name === "TheDiscoveryFeed") {
      router.push("/sign-in?redirectToPage=plus");
    } else {
      Session.destroy();
      window.location.href = "/sign-in";
    }
  }
  // eslint-disable-next-line no-prototype-builtins
  const [isCatPage, isProductPage] = [to.params.hasOwnProperty("category"), to.params.hasOwnProperty("productName")];
  // Setting document title for SEO
  let title = "Bulletin - Wholesale and Retail";
  if (to.meta.title) {
    title += ` - ${to.meta.title} `;
  } else if (isCatPage) {
    title += ` - ${to.params.category}`;
  } else if (isProductPage) {
    title += ` - ${to.params.productName}`;
  }
  document.title = title;
});

router.afterEach((to) => {
  /*
   * after navigating away dismiss any open modals.
   * the `afterEach` block will not affect any navigation in case this block throws an error.
   */

  Utils.viewTracking(to.path);

  const excludedPath = ["/checkout"];
  if (Vue.swal.isVisible && !excludedPath.includes(to.path)) {
    Vue.swal.close();
  }
});

const initializeLogRocket = () => {
  const user = Session.getUserInfo();

  if (user && user?.role !== "RETAILER") {
    return;
  }

  if (!user && window.location.pathname !== "/onboarding") {
    return;
  }

  if (Env.getOrigin() === "prod") {
    LogRocket.init("obthka/bulletin", {
      console: {
        log: true,
        info: false,
        debug: false,
        warn: true,
        error: true,
      },
      shouldAggregateConsoleErrors: true,
      shouldDebugLog: false,
    });

    if (user) {
      LogRocket.identify(user.id, {
        name: user.email, // duplicated entry because there is no name related param in user info object
        email: user.email,
      });
    }
  }
};

new Vue({
  metaInfo: {
    script: [
      {
        src: `https://maps.googleapis.com/maps/api/js?key=${Env.getGoogleMapsApiKey()}&libraries=places&language=en-US`,
        once: true,
        defer: true,
      },
    ],
  },
  router,
  store,
  apolloProvider,
  data() {
    return {
      // eslint-disable-next-line no-useless-escape
      emailRegex:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      // eslint-disable-next-line no-useless-escape
      passwordPattern: /^.*(?=.{8,})(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(^[a-zA-Z0-9@\$=!:.#%]+$)/,
      alphaNumericPattern: /^[a-z0-9]+$/i,
      alphaNumericSpacePattern: /^[a-z\d\-_\s]+$/i,
      vanityURLPattern: /^[a-z0-9-]+$/,
      showBanner: false,
    };
  },
  beforeMount() {
    const user = Session.getUserInfo();
    if (user) {
      this.$utils.initMixpanelBeforeTrack(this);
    }
  },
  mounted() {
    const user = Session.getUserInfo();
    if (user) {
      this.$store.commit("setUser", user);
      new PusherConfig(this);
      if (user.role !== "BRAND") {
        this.$store.dispatch("cart/getCartProductCount");
      }
      if (user.role !== "ADMIN") {
        createTalkSession(this, user);
      }
    }

    if (user?.role === "RETAILER") {
      this.$utils.balanceChangeHandler(Session, this);
      this.$utils.setRetailerStores(this);
    }

    this.$utils.checkUtmParamsAndRedirect(this.$router);
    initializeLogRocket();
  },
  render: (render) => render(App),
}).$mount("#app");
